import { GridLegacy as Grid, Stack } from '@mui/material'
import { ContentChip, FormSelectField, FormTemplate, FormTextField, InfoBox, InfoText, tokens, validators } from 'ComponentLibrary'
import { PaperPlaneTiltIcon } from '@phosphor-icons/react'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { Control, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type ExternalAppraiserFormValues = {
    salutation: string
    firstName: string
    lastName: string
    organization_name: string
    email: string
}

type ExternalAppraiserStepTwoProps = {
    control: Control<ExternalAppraiserFormValues>
    errors: FieldErrors<ExternalAppraiserFormValues>
    disabled?: boolean
}

export function ExternalAppraiserStepTwo({ control, errors, disabled = false }: ExternalAppraiserStepTwoProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser.form' })
    const { t: tExternalAppraiser } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const requiredMessage = t('requiredField')
    const salutationOptions = [
        { label: t('salutationOptions.mr'), value: 'Herr' },
        { label: t('salutationOptions.ms'), value: 'Frau' },
        { label: t('salutationOptions.dr'), value: 'Dr.' },
        { label: t('salutationOptions.placeholder'), value: '' },
    ]

    return (
        <Stack width="100%" gap={tokens.SizingSpacing400}>
            <Stack
                gap={tokens.SizingSpacing100}
                padding={tokens.SizingSpacing150}
                borderRadius={tokens.SizingCornerM}
                bgcolor={tokens.ColorGraphSequentialTenLightest}
            >
                <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                    {t('betaTitle')}
                </Typography>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    <span style={{ ...tokens.TextSEmphasis, color: tokens.ColorTextBold }}>{t('betaTextHighlight')}</span> {t('betaText')}
                </Typography>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    <span style={{ ...tokens.TextSEmphasis, color: tokens.ColorTextBold }}>{t('emailTextHighlight')}</span> {t('emailText')}
                </Typography>
            </Stack>

            <Stack gap={tokens.SizingSpacing150}>
                <Headline title={t('title')} variant="headline3" />
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    {t('description')}
                </Typography>
            </Stack>
            <InfoBox variant="ordinary" icon={<PaperPlaneTiltIcon />}>
                <Stack gap={tokens.SizingSpacing050}>
                    <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                        {t('invitationNotice.title')}
                    </Typography>
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {t('invitationNotice.description')}
                    </Typography>
                </Stack>
            </InfoBox>
            <FormTemplate hideFooter dense>
                <FormSelectField
                    label={tTerminology('form.salutation')}
                    errors={errors}
                    control={control}
                    fieldName="salutation"
                    options={salutationOptions}
                    xs={6}
                    disabled={disabled}
                    required
                    requiredMessage={requiredMessage}
                />
                <Grid item xs={6} />
                <FormTextField
                    control={control}
                    fieldName="firstName"
                    errors={errors}
                    label={tTerminology('form.firstName')}
                    xs={6}
                    disabled={disabled}
                />
                <FormTextField
                    control={control}
                    fieldName="lastName"
                    errors={errors}
                    label={tTerminology('form.lastName')}
                    xs={6}
                    disabled={disabled}
                    required
                    requiredMessage={requiredMessage}
                />
                <FormTextField
                    control={control}
                    fieldName="organization_name"
                    errors={errors}
                    label={t('organization')}
                    xs={6}
                    disabled={disabled}
                />
                <FormTextField
                    control={control}
                    fieldName="email"
                    errors={errors}
                    label={tTerminology('form.email')}
                    xs={6}
                    required
                    type="email"
                    rules={{ validate: (value) => validators.isEmail(value.trim()) || t('invalidEmail') }}
                    requiredMessage={requiredMessage}
                    disabled={disabled}
                />
                <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flexWrap="wrap" minWidth={0}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flexWrap="wrap" minWidth={0}>
                            <InfoText variant="information" size="small" text={t('lockedInfo')} />
                            <ContentChip size="small" color="purple" label={tExternalAppraiser('beta')} />
                        </Stack>
                    </Stack>
                </Grid>
            </FormTemplate>
        </Stack>
    )
}
